import {yupResolver} from "@hookform/resolvers/yup"
import {
  Box,
  IconButton,
  InputBase,
  InputBaseProps,
  MenuItem,
  Select,
  Stack,
  styled,
  Typography,
} from "@mui/material"
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import {FC, Fragment, PropsWithChildren, useEffect, useState} from "react"
import {Controller, useForm} from "react-hook-form"
import {decodeToken} from "react-jwt"
import {toast} from "react-toastify"
import {useLocalStorage, useSessionStorage} from "usehooks-ts"
import * as yup from "yup"
import {
  ChevronDownIcon,
  ErrorIcon,
  InfoIcon,
  ReportProblemIcon,
  SuccessIcon,
} from "../../../assets"
import {useConfigProvider} from "../../../config"
import {appInsights} from "../../../config/appInsights"
import {stringIsLink, stringIsNotSQL, stringIsScript} from "../../../helpers"
import {accountEndpoints, fabricEndpoints} from "../../../services"
import {
  Contact,
  OnboardingRequest,
  Token,
  UpdateCRMContact,
} from "../../../types"
import {NewButton, Tooltip} from "../../atoms"
import {TextWithTooltip} from "../../molecules"
import {langs} from "../../../pages/Profile/Preferences/constants"
import {useTranslation} from "react-i18next"
import {useExpiringLocalStorage} from "../../../hooks/useExpiringLocalStorage"

const schema = yup.object({
  firstName: yup
    .string()
    .required("Required field")
    .min(2, "First name is not valid")
    .max(50, "Character limit reached")
    .matches(stringIsLink, "First name is not valid")
    .matches(stringIsNotSQL, "First name is not valid")
    .matches(stringIsScript, "First name is not valid"),
  lastName: yup
    .string()
    .required("Required field")
    .min(2, "Last name is not valid")
    .max(50, "Character limit reached")
    .matches(stringIsLink, "Last name is not valid")
    .matches(stringIsNotSQL, "Last name is not valid")
    .matches(stringIsScript, "Last name is not valid"),
  jobTitle: yup
    .string()
    .required("Required field")
    .min(2, "Job title is not valid")
    .max(128, "Character limit reached")
    .matches(stringIsLink, "Job title is not valid")
    .matches(stringIsNotSQL, "Job title is not valid")
    .matches(stringIsScript, "Job title is not valid"),
  jobFunction: yup.string().required("Required field"),
  workPhoneNumber: yup
    .string()
    .required("Required field")
    .min(10, "Number is not valid")
    .max(15, "Character limit reached")
    .matches(/^[0-9\s()+-]*$/, "Number is not valid"),
  language: yup.string().required("Required field"),
})

type FormValues = yup.InferType<typeof schema>

type Props = {
  handleToggleEdit: () => void
  profileInfo: Contact
}

const ProfileInfoForm: FC<Props> = (props) => {
  const {handleToggleEdit, profileInfo} = props

  const {completeOnboarding, getJobFunctions, updateContact} =
    accountEndpoints()
  const {sendFabricEvents} = fabricEndpoints()
  const {token} = useConfigProvider()
  const decodedToken = decodeToken<Token>(token)
  const queryClient = useQueryClient()
  const [_, setName] = useLocalStorage("name", "")
  const [__, setLanguage] = useSessionStorage("language", "en")
  const [getTempLanguage, setTempLanguage] = useExpiringLocalStorage<string>(
    "tempLanguage",
    "",
    10 * 60 * 1000, //expire in 10 minutes
  )

  const {t} = useTranslation()
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const {handleSubmit, control, reset} = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      firstName: profileInfo.firstName,
      lastName: profileInfo.lastName,
      jobTitle: profileInfo.jobTitle,
      jobFunction: profileInfo.jobFunction,
      workPhoneNumber: profileInfo.businessPhone,
      language: profileInfo.preferredLanguage?.toString(),
    },
  })

  const getJobFunctionsQuery = useQuery({
    queryKey: ["jobFunctions"],
    queryFn: getJobFunctions,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const jobFunctions = (getJobFunctionsQuery.data ?? []).sort((a, b) => {
    return a.jobFunction.localeCompare(b.jobFunction)
  })

  const updateContactMutation = useMutation({
    mutationFn: updateContact,
    mutationKey: ["updateContact"],
  })

  const completeOnboardingMutation = useMutation({
    mutationFn: completeOnboarding,
    mutationKey: ["completeOnboarding"],
  })

  const sendFabricEventsMutation = useMutation({
    mutationFn: sendFabricEvents,
    mutationKey: ["sendFabricEvents"],
  })

  const handleEditProfile = async (data: FormValues) => {
    if (!profileInfo.emailAddress) return

    const updatePayload = {
      _cr_accountname_value:
        profileInfo.accountId || profileInfo.accountName || "",
      contactid: profileInfo.contactId,
      emailaddress1: profileInfo.emailAddress,
      firstname: data.firstName,
      lastname: data.lastName,
      telephone1: data.workPhoneNumber,
      Jobtitle: data.jobTitle,
      new_jobfunction: data.jobFunction,
      cr_language: data.language,
    } satisfies UpdateCRMContact

    const onboardingPayload = {
      email: profileInfo.emailAddress,
      firstName: data.firstName,
      lastName: data.lastName,
      jobTitle: data.jobTitle,
      jobFunction: data.jobFunction,
      phone: data.workPhoneNumber,
      contactId: profileInfo.contactId,
    } satisfies OnboardingRequest

    await updateContactMutation.mutateAsync(updatePayload, {
      onError: () => {
        toast.error(
          t("An unexpected error occurred. Please try refresh your page."),
          {
            autoClose: 3000,
            icon: ErrorIcon,
            toastId: "api-error-toast",
          },
        )
      },
      onSuccess: async () => {
        await completeOnboardingMutation.mutateAsync(onboardingPayload, {
          onError: () => {
            toast.error(
              t("An unexpected error occurred. Please try refresh your page."),
              {
                autoClose: 3000,
                icon: ErrorIcon,
                toastId: "api-error-toast",
              },
            )
          },
          onSuccess: async () => {
            toast.success("Successful! Your request has been submited", {
              autoClose: 3000,
              style: {
                borderLeftColor: "#58A733",
              },
              icon: SuccessIcon,
            })

            queryClient.setQueryData<Contact>(
              ["profileInfo", profileInfo.emailAddress],
              (oldData) => {
                if (!oldData) return undefined
                return {
                  ...oldData,
                  firstName: data.firstName,
                  lastName: data.lastName,
                  jobTitle: data.jobTitle,
                  jobFunction: data.jobFunction,
                  businessPhone: data.workPhoneNumber,
                }
              },
            )

            setName(`${data.firstName} ${data.lastName}`.trim())

            handleToggleEdit()

            appInsights.trackEvent({
              name: "UPDATE_PROFILE",
              properties: {
                userId: profileInfo.contactId,
                userName: `${data.firstName} ${data.lastName}`,
                email: profileInfo.emailAddress,
                companyId: decodedToken?.companyId,
                accountName: profileInfo.accountName,
                payload: {
                  ...onboardingPayload,
                },
              },
            })

            await sendFabricEventsMutation.mutateAsync([
              {
                type: "Update_contact",
                targetId: profileInfo.contactId,
                targetName: `${data.firstName} ${data.lastName}`,
                appUrl: window.location.href,
              },
            ])
          },
        })

        setLanguage(
          langs.find((lang) => lang.value === data.language)?.code || "en",
        )
        setTempLanguage(
          langs.find((lang) => lang.value === data.language)?.code || "en",
        )
      },
    })
  }

  const handleCancel = () => {
    handleToggleEdit()
  }

  useEffect(() => {
    reset({
      language:
        langs.find((lang) => lang.code === getTempLanguage())?.value ||
        profileInfo.preferredLanguage?.toString(),
    })
  }, [getTempLanguage(), profileInfo.preferredLanguage])

  return (
    <Stack
      component="form"
      id="profile-info-form"
      aria-label="Profile Info Form"
      onSubmit={handleSubmit(handleEditProfile)}
      direction="column"
      width="100%"
      gap="24px"
    >
      <Stack component="section" direction="column" gap="8px">
        <Controller
          control={control}
          name="firstName"
          render={({field, fieldState}) => (
            <FormGroup>
              <Input
                {...field}
                id="firstName"
                aria-label="First name"
                placeholder="Enter first name"
                error={!!fieldState.error}
                sx={{
                  width: "100%",
                  input: {
                    paddingX: "16px",
                    paddingY: "6px",
                    fontSize: "14px",
                    lineHeight: "20px",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: fieldState.error ? "#DB0D00" : "#BDBDBC",
                    borderRadius: "4px",
                    color: "#242d35",
                    height: "40px",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s ease-in-out",

                    "::placeholder": {
                      color: "#7A7A79",
                    },
                  },
                }}
              />
              {fieldState.error ? (
                <ErrorMessage>{t(fieldState.error.message || "")}</ErrorMessage>
              ) : null}
            </FormGroup>
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({field, fieldState}) => (
            <FormGroup>
              <Input
                {...field}
                id="lastName"
                aria-label="Last name"
                placeholder="Enter last name"
                error={!!fieldState.error}
                sx={{
                  width: "100%",
                  input: {
                    paddingX: "16px",
                    paddingY: "6px",
                    fontSize: "14px",
                    lineHeight: "20px",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: fieldState.error ? "#DB0D00" : "#BDBDBC",
                    borderRadius: "4px",
                    color: "#242d35",
                    height: "40px",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s ease-in-out",

                    "::placeholder": {
                      color: "#7A7A79",
                    },
                  },
                }}
              />
              {fieldState.error ? (
                <ErrorMessage>{t(fieldState.error.message || "")}</ErrorMessage>
              ) : null}
            </FormGroup>
          )}
        />
      </Stack>
      <Stack component="section" direction="column" gap="16px">
        <Controller
          control={control}
          name="jobTitle"
          render={({field, fieldState}) => (
            <FormGroup>
              <InputLabel htmlFor="jobTitle" error={!!fieldState.error}>
                {t("Job title")}
              </InputLabel>
              <Input
                {...field}
                id="jobTitle"
                aria-label="Job title"
                placeholder="Enter job title"
                error={!!fieldState.error}
                sx={{
                  width: "100%",
                  input: {
                    paddingX: "16px",
                    paddingY: "6px",
                    fontSize: "14px",
                    lineHeight: "20px",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: fieldState.error ? "#DB0D00" : "#BDBDBC",
                    borderRadius: "4px",
                    color: "#242d35",
                    height: "40px",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s ease-in-out",

                    "::placeholder": {
                      color: "#7A7A79",
                    },
                  },
                }}
              />
              {fieldState.error ? (
                <ErrorMessage>{t(fieldState.error.message || "")}</ErrorMessage>
              ) : null}
            </FormGroup>
          )}
        />
        <Controller
          control={control}
          name="jobFunction"
          render={({field, fieldState}) => (
            <FormGroup>
              <InputLabel htmlFor="jobFunction" error={!!fieldState.error}>
                {t("Job function")}
              </InputLabel>
              <Select
                {...field}
                IconComponent={ChevronDownIcon}
                sx={{
                  ".MuiSelect-icon": {
                    right: "20px",
                  },
                  ".MuiInputBase-input": {
                    paddingTop: "7px",
                    paddingBottom: "8px",
                    paddingLeft: "16px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease-in-out",
                  },
                  ".MuiOutlinedInput-input": {
                    borderStyle: "solid",
                    borderWidth: "1px",
                    borderColor: fieldState.error ? "#DB0D00" : "#BDBDBC",
                  },
                  ".Mui-disabled": {
                    color: "#242d35",
                    "-webkit-text-fill-color": "#242d35",
                    opacity: 1,
                    backgroundColor: "#BDBDBC",

                    "::placeholder": {
                      color: "#7A7A79",
                      "-webkit-text-fill-color": "#7A7A79",
                    },
                  },
                  fieldset: {
                    border: "none",
                  },
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  sx: {
                    ".MuiPaper-root": {
                      boxShadow:
                        "0px 0px 2px 0px #0000001F, 0px 4px 8px 0px #00000024",
                      border: "1px solid #EEEEEE",
                      padding: "0",
                      backgroundColor: "#FFFFFF",
                    },
                  },
                  slotProps: {
                    paper: {
                      sx: {
                        maxHeight: "160px",
                      },
                    },
                  },
                }}
                displayEmpty
                renderValue={(value) => {
                  const hasValue = !!value || !!profileInfo.jobFunction
                  if (!hasValue)
                    return (
                      <Typography
                        component="span"
                        fontSize="14px"
                        fontWeight={400}
                        color="#7A7A79"
                        lineHeight="1.43"
                        textAlign="left"
                        sx={{
                          "-webkit-text-fill-color": "#7A7A79",
                        }}
                      >
                        Select job function
                      </Typography>
                    )
                  return (
                    <Typography
                      component="span"
                      fontSize="14px"
                      fontWeight={400}
                      color="#242d35"
                      lineHeight="1.43"
                      textAlign="left"
                      sx={{
                        "-webkit-text-fill-color": "#242d35",
                      }}
                    >
                      {value || profileInfo.jobFunction}
                    </Typography>
                  )
                }}
              >
                {jobFunctions?.map((jobFunction) => (
                  <MenuItem
                    key={jobFunction.id}
                    value={jobFunction.jobFunction}
                    sx={{
                      fontSize: "14px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease-in-out",
                      backgroundColor:
                        field.value === jobFunction.jobFunction
                          ? "#DCEBF3"
                          : "#FFFFFF",
                      ":hover": {
                        backgroundColor:
                          field.value === jobFunction.jobFunction
                            ? "#DCEBF3"
                            : "#F5F5F5",
                      },
                    }}
                    disableRipple
                  >
                    <Typography
                      component="span"
                      fontSize="14px"
                      fontWeight={400}
                      color="#242d35"
                      lineHeight="1.43"
                      textAlign="left"
                    >
                      {jobFunction.jobFunction}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              <ErrorMessage>
                {fieldState.error ? t(fieldState.error.message || "") : null}
              </ErrorMessage>
            </FormGroup>
          )}
        />
        <Stack
          component="article"
          direction="column"
          gap="5px"
          id="emailAddress"
        >
          <Typography
            fontSize="16px"
            fontWeight="400"
            color="#224957"
            lineHeight="normal"
          >
            {t("Email address")}
          </Typography>
          <Typography
            fontSize="14px"
            fontWeight="400"
            color="#595958"
            lineHeight="normal"
          >
            <TextWithTooltip
              text={profileInfo.emailAddress || ""}
              limit={22}
              includeEllipsis={false}
              textStyle={{
                fontSize: "14px",
                fontWeight: "400",
                color: "#595958",
                lineHeight: "normal",
              }}
              tooltipProps={{
                arrow: true,
              }}
              tooltipStyle={{
                "& .MuiTooltip-tooltip": {
                  paddingX: "12px",
                  paddingY: "6px",
                },
              }}
            />
          </Typography>
        </Stack>
        <Stack
          component="article"
          direction="column"
          gap="5px"
          id="accountName"
        >
          <Typography
            fontSize="16px"
            fontWeight="400"
            color="#224957"
            lineHeight="normal"
          >
            {t("Account name")}
          </Typography>
          <Typography
            fontSize="14px"
            fontWeight="400"
            color="#595958"
            lineHeight="normal"
          >
            <TextWithTooltip
              text={profileInfo.accountName || ""}
              limit={22}
              includeEllipsis={false}
              textStyle={{
                fontSize: "14px",
                fontWeight: "400",
                color: "#595958",
                lineHeight: "normal",
              }}
              tooltipProps={{
                arrow: true,
              }}
              tooltipStyle={{
                "& .MuiTooltip-tooltip": {
                  paddingX: "12px",
                  paddingY: "6px",
                },
              }}
            />
          </Typography>
        </Stack>
        <Controller
          control={control}
          name="workPhoneNumber"
          render={({field, fieldState}) => (
            <FormGroup>
              <InputLabel htmlFor="workPhoneNumber" error={!!fieldState.error}>
                {t("Work phone number")}
              </InputLabel>
              <Input
                {...field}
                id="workPhoneNumber"
                aria-label="Work phone number"
                placeholder="Enter phone number"
                error={!!fieldState.error}
                sx={{
                  width: "100%",
                  input: {
                    paddingX: "16px",
                    paddingY: "6px",
                    fontSize: "14px",
                    lineHeight: "20px",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: fieldState.error ? "#DB0D00" : "#BDBDBC",
                    borderRadius: "4px",
                    color: "#242d35",
                    height: "40px",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s ease-in-out",

                    "::placeholder": {
                      color: "#7A7A79",
                    },
                  },
                }}
              />
              {fieldState.error ? (
                <ErrorMessage>{t(fieldState.error.message || "")}</ErrorMessage>
              ) : null}
            </FormGroup>
          )}
        />
        <Controller
          control={control}
          name="language"
          render={({field, fieldState}) => (
            <FormGroup>
              <Stack direction={"row"} gap={"8px"}>
                <InputLabel htmlFor="language" error={!!fieldState.error}>
                  {t("Preferred language")}
                </InputLabel>
                <Tooltip
                  title={t(
                    "Selecting a preferred language will change your default language in MyControlRisks.",
                  )}
                  arrow
                  placement="right"
                  open={tooltipOpen}
                  onClose={() => setTooltipOpen(false)}
                >
                  <IconButton
                    onClick={() => setTooltipOpen((prev) => !prev)}
                    aria-label="info"
                    disableRipple
                    disableTouchRipple
                    disableFocusRipple
                    sx={{p: 0}}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Select
                {...field}
                defaultValue={
                  langs?.find(
                    (lang) =>
                      lang?.value === profileInfo.preferredLanguage?.toString(),
                  )?.value ||
                  langs?.[0]?.value ||
                  ""
                }
                IconComponent={ChevronDownIcon}
                sx={{
                  ".MuiSelect-icon": {
                    right: "20px",
                  },
                  ".MuiInputBase-input": {
                    paddingTop: "7px",
                    paddingBottom: "8px",
                    paddingLeft: "16px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease-in-out",
                  },
                  ".MuiOutlinedInput-input": {
                    borderStyle: "solid",
                    borderWidth: "1px",
                    borderColor: fieldState.error ? "#DB0D00" : "#BDBDBC",
                  },
                  ".Mui-disabled": {
                    color: "#242d35",
                    "-webkit-text-fill-color": "#242d35",
                    opacity: 1,
                    backgroundColor: "#BDBDBC",

                    "::placeholder": {
                      color: "#7A7A79",
                      "-webkit-text-fill-color": "#7A7A79",
                    },
                  },
                  fieldset: {
                    border: "none",
                  },
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  sx: {
                    ".MuiPaper-root": {
                      boxShadow:
                        "0px 0px 2px 0px #0000001F, 0px 4px 8px 0px #00000024",
                      border: "1px solid #EEEEEE",
                      padding: "0",
                      backgroundColor: "#FFFFFF",
                    },
                  },
                  slotProps: {
                    paper: {
                      sx: {
                        maxHeight: "160px",
                      },
                    },
                  },
                }}
                displayEmpty
                renderValue={(value) => {
                  const hasValue = !!value || !!profileInfo.jobFunction
                  if (!hasValue)
                    return (
                      <Typography
                        component="span"
                        fontSize="14px"
                        fontWeight={400}
                        color="#7A7A79"
                        lineHeight="1.43"
                        textAlign="left"
                        sx={{
                          "-webkit-text-fill-color": "#7A7A79",
                        }}
                      >
                        Select preferred language
                      </Typography>
                    )
                  return (
                    <Typography
                      component="span"
                      fontSize="14px"
                      fontWeight={400}
                      color="#242d35"
                      lineHeight="1.43"
                      textAlign="left"
                      sx={{
                        "-webkit-text-fill-color": "#242d35",
                      }}
                    >
                      {langs.find((lang) => lang.value === value)?.label ||
                        langs.find(
                          (lang) =>
                            lang.value ===
                            profileInfo.preferredLanguage?.toString(),
                        )?.label}
                    </Typography>
                  )
                }}
              >
                {langs?.map((lang) => (
                  <MenuItem
                    key={lang.value}
                    value={lang.value}
                    sx={{
                      fontSize: "14px",
                      padding: "8px 16px",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease-in-out",
                      backgroundColor:
                        field.value === lang.value ? "#DCEBF3" : "#FFFFFF",
                      ":hover": {
                        backgroundColor:
                          field.value === lang.value
                            ? "#DCEBF3 !important"
                            : "#F5F5F5 !important",
                      },
                    }}
                    disableRipple
                  >
                    <Typography
                      component="span"
                      fontSize="14px"
                      fontWeight={400}
                      color="#242d35"
                      lineHeight="1.43"
                      textAlign="left"
                    >
                      {lang.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              <ErrorMessage>
                {fieldState.error ? t(fieldState.error.message || "") : null}
              </ErrorMessage>
            </FormGroup>
          )}
        />
      </Stack>
      <Stack component="footer" direction="column" gap="8px">
        <NewButton
          type="submit"
          variant="filled"
          color="primary"
          size="medium"
          fullWidth
          text={t("Save")}
          disabled={
            updateContactMutation.isPending ||
            completeOnboardingMutation.isPending
          }
        />
        <NewButton
          type="button"
          variant="outline"
          color="primary"
          size="medium"
          fullWidth
          text={t("Cancel")}
          onClick={handleCancel}
        />
      </Stack>
    </Stack>
  )
}

ProfileInfoForm.displayName = "ProfileInfoForm"
export default ProfileInfoForm

const CustomInput = styled(InputBase)<InputBaseProps>(({theme, error}) => ({
  width: "100%",
  input: {
    padding: "8px 16px",
    fontSize: "16px",
    lineHeight: "24px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: error ? "#DB0D00" : "#BDBDBC",
    borderRadius: "4px",
    color: "#242d35",
    height: "40px",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease-in-out",

    "::placeholder": {
      color: "#7A7A79",
    },

    "&:disabled": {
      backgroundColor: "#BDBDBC",
      borderColor: "#BDBDBC",
      color: "#242d35",
      "-webkit-text-fill-color": "#242d35",
    },
  },
}))

export const ErrorMessage: FC<PropsWithChildren> = (props) => {
  const {children} = props
  return children ? (
    <Stack
      component="span"
      flexDirection="row"
      gap="4px"
      minHeight="20px"
      alignItems="center"
      sx={{
        svg: {
          flexShrink: 0,
        },
      }}
    >
      <Fragment>
        <ReportProblemIcon />
        <Typography
          aria-label="error-message"
          component="span"
          fontSize="16px"
          fontWeight={400}
          lineHeight="24px"
          color="#DB0D00"
        >
          {children}
        </Typography>
      </Fragment>
    </Stack>
  ) : null
}

type InputLabelProps = {
  error?: boolean
  required?: boolean
  htmlFor?: string
}
export const InputLabel: FC<PropsWithChildren<InputLabelProps>> = (props) => {
  const {children, error, required, htmlFor} = props
  return (
    <Stack
      component="label"
      htmlFor={htmlFor}
      flexDirection="row"
      gap="4px"
      minHeight="24px"
    >
      <Typography
        component="span"
        fontSize="16px"
        fontWeight={400}
        lineHeight={1.5}
        color={error ? "#DB0D00" : "#053747"}
        sx={{
          transition: "color 0.2s ease-in-out",
        }}
      >
        {children}
      </Typography>
      {required ? (
        <Typography
          component="span"
          fontSize="16px"
          fontWeight={400}
          lineHeight="20px"
          color="#DB0D00"
        >
          *
        </Typography>
      ) : null}
    </Stack>
  )
}

export const FormGroup = styled("article")(({theme}) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flexWrap: "nowrap",
  flex: 1,
}))

export const Input: FC<InputBaseProps> = (props) => {
  return (
    <Box
      width="100%"
      position="relative"
      sx={{
        "&:focus-within": {
          ".focus-indicator": {
            opacity: props.error ? 0 : 1,
          },
        },
      }}
    >
      <CustomInput {...props} />
      <Box
        className="focus-indicator"
        sx={{
          position: "absolute",
          bottom: "0",
          left: "0",
          right: "0",
          height: "1.6px",
          width: "100%",
          opacity: 0,
          backgroundColor: "#7A7A79",
          borderBottomLeftRadius: "2px",
          borderBottomRightRadius: "2px",
          transition: "opacity 0.2s ease-in-out",
        }}
      />
    </Box>
  )
}
